import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import AvatarWithFlag from 'components/AvatarWithFlag/AvatarWithFlag';
import Points from 'components/Points';
import UsernameLink from 'components/UsernameLink/UsernameLink';
import { useWindowSize } from 'hooks/useWindowSize';
import { useSuggestionPassContext } from '../../TradeOffers/context/SuggestionPassContext';
import { useGetMeQuery } from 'store/services/user/meApi';
import { TradeSuggestion } from 'types/interfaces/tradeSuggestion';
import SuggestionActions from './SuggestionActions';
import SuggestionAxis from './SuggestionAxis';
import SuggestionCountdown from './SuggestionCountdown';
import SuggestionLedger from './SuggestionLedger';
import SuggestionHelpBlock from './SuggestionHelpBlock';
import SuggestionNet from './SuggestionNet';
import { SuggestionsEmptyAllHandled } from './SuggestionsEmptyStates';

/** Matches the pass animation in TradeSuggestionsPage.css (330ms out, 340ms in) */
const PASS_ANIMATION_MS = 340;

/** Below this the ledger opens as a bottom sheet instead of taking the axis's place */
const SHEET_BREAKPOINT = 768;

interface Props {
  suggestions: TradeSuggestion[];
}

/**
 * The deck: one suggestion filling the content area, the rest peeking behind it
 * as stacked edges.
 *
 * IDENTITY IS BY id, NEVER BY INDEX. This is load-bearing. A deferred pass fires
 * dismiss after its undo window, which invalidates the 'Suggestions' tag and
 * refetches the list WITHOUT the dismissed row. An index cursor would then point
 * one suggestion further along and silently skip one, every single pass.
 *
 * Local `queue` is therefore the source of truth for order and membership, and
 * the server response is reconciled into it by id.
 *
 * The set of passed ids is NOT local: it lives in SuggestionPassProvider, above
 * the Offers tab host. This deck unmounts on a tab switch while a pass is still
 * inside its undo window, so an id set held here would reset and the passed
 * suggestion — still ACTIVE server-side, because its dismiss has not fired —
 * would pop back into the hand on return.
 */
const SuggestionDeck: React.FC<Props> = ({ suggestions }) => {
  const [queue, setQueue] = useState<TradeSuggestion[]>(suggestions);
  const [activeId, setActiveId] = useState<string | null>(suggestions[0]?.id ?? null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const { data: me } = useGetMeQuery();
  const { passedIds, lastRestoredId, pass } = useSuggestionPassContext();

  const { width } = useWindowSize(0);
  // An inline expansion pushes the action buttons off a 390px screen, so below
  // the breakpoint the ledger becomes a sheet and the card stays put. This has
  // to be a JS check, not a media query: it changes which JSX renders in three
  // separate places, not how one element is styled.
  const useSheet = (width || 1500) < SHEET_BREAKPOINT;

  // Reconcile a refetch into the local queue.
  useEffect(() => {
    setQueue((current) => {
      const incoming = new Map(suggestions.map((s) => [s.id, s]));
      // Keep local order; refresh each surviving entry from the server. Passed
      // ids are retained even once the server stops returning them, because
      // they are the numbering history.
      const kept = current
        .filter((s) => incoming.has(s.id) || passedIds.has(s.id))
        .map((s) => incoming.get(s.id) ?? s);
      const keptIds = new Set(kept.map((s) => s.id));
      // Append genuinely new ids. A suggestion inside its undo window is NOT
      // new — it is still in the server response because its dismiss has not
      // fired yet — so appending it would pop it back into the deck.
      const appended = suggestions.filter((s) => !keptIds.has(s.id) && !passedIds.has(s.id));
      return [...kept, ...appended];
    });
  }, [suggestions, passedIds]);

  const firstUnhandled = useMemo(
    () => queue.find((s) => !passedIds.has(s.id)) ?? null,
    [queue, passedIds]
  );

  // Undo can be clicked from any tab, including while this deck is unmounted.
  // On return, jump to whatever was restored rather than wherever the cursor
  // happened to be.
  useEffect(() => {
    if (lastRestoredId && queue.some((s) => s.id === lastRestoredId)) {
      setActiveId(lastRestoredId);
    }
  }, [lastRestoredId, queue]);

  // Keep activeId pointing at an unhandled suggestion.
  useEffect(() => {
    if (!activeId || passedIds.has(activeId) || !queue.some((s) => s.id === activeId)) {
      setActiveId(firstUnhandled ? firstUnhandled.id : null);
    }
  }, [queue, activeId, firstUnhandled, passedIds]);

  const activeIndex = queue.findIndex((s) => s.id === activeId);
  const active = activeIndex >= 0 ? queue[activeIndex] : null;

  const handlePass = useCallback(() => {
    if (!active || isAnimating) return;
    setIsAnimating(true);
    pass(active.id, active.counterparty.username);
    // A ledger left open would carry over to the next suggestion's card.
    setLedgerOpen(false);
    // Advance to the next unpassed suggestion after this one.
    const next = queue.find((s, i) => i > activeIndex && !passedIds.has(s.id));
    setActiveId(next ? next.id : null);
    // The lockout is what stops a double click from skipping a suggestion.
    window.setTimeout(() => setIsAnimating(false), PASS_ANIMATION_MS);
  }, [active, activeIndex, isAnimating, pass, queue, passedIds]);

  // The designed empty state fills the surface on its own: no header bar and
  // no help block behind it, matching the mock.
  if (!active) return <SuggestionsEmptyAllHandled variant="page" />;

  // Position through the hand, matching the approved design: the denominator is
  // the whole hand and stays put as the user works through it.
  const total = queue.length;
  const position = activeIndex + 1;
  const remaining = queue.filter((s) => !passedIds.has(s.id)).length;
  const edges = Math.min(2, remaining - 1);

  // Desktop only: opening the ledger takes the axis's place, so the net moves up
  // into the partner row and the countdown gives up its slot. As a sheet the
  // card is untouched, so the countdown stays visible — which is also what the
  // "countdown is always visible" rule wants wherever it can be honoured.
  const ledgerReplacesAxis = ledgerOpen && !useSheet;

  return (
    <div className="sugg-deck">
      {/* One 58px header bar. The page <h1> is the feature name (FEATURE_NAME
          in utils/glossary), which owns the title for this surface now — a
          second heading here was a duplicate. */}
      <header className="sugg-headerbar">
        <span className="sugg-headerbar-count">
          {position} of {total}
        </span>
        <div className="sugg-headerbar-progress" role="presentation">
          <div
            className="sugg-headerbar-progress-fill"
            style={{ width: `${(position / total) * 100}%` }}
          />
        </div>
        <span className="sugg-headerbar-progress-label">Reviewed one at a time</span>
        <div className="sugg-headerbar-balance">
          <Points type="big" points={me?.points ?? 0} isLoading={!me} />
        </div>
      </header>

      <div className="sugg-deck-stage">
        {/* Deck edges sell "more waiting behind this one" and are the reason
            the surface reads as a hand rather than a single card. */}
        {Array.from({ length: edges }, (_, i) => (
          <div key={`edge-${i}`} className={`sugg-deck-edge sugg-deck-edge--${i + 1}`} />
        ))}

        <article key={active.id} className={`sugg-card${isAnimating ? ' sugg-card--passing' : ''}`}>
          <header className="sugg-card-header">
            <AvatarWithFlag
              avatarUrl={active.counterparty.avatarUrl}
              username={active.counterparty.username}
              size={32}
              isElite={active.counterparty.isElite}
              showAnimatedBorder={active.counterparty.showAnimatedBorder}
              borderColor1={active.counterparty.borderColor1}
              borderColor2={active.counterparty.borderColor2}
              alt=""
            />
            <div className="sugg-card-who">
              <UsernameLink
                username={active.counterparty.username}
                isElite={active.counterparty.isElite}
              />
              <span className="sugg-card-trust">
                {active.counterparty.stats.rating != null
                  ? `★ ${active.counterparty.stats.rating.toFixed(1)} (${active.counterparty.stats.ratingCount})`
                  : 'No ratings yet'}
                {` · ${active.counterparty.stats.cardsSent.toLocaleString()} cards sent`}
              </span>
            </div>
            {ledgerReplacesAxis ? (
              <>
                <SuggestionNet suggestion={active} inline />
                <button
                  type="button"
                  className="sugg-card-ledger-hide"
                  aria-expanded
                  onClick={() => setLedgerOpen(false)}
                >
                  Hide the full ledger
                </button>
              </>
            ) : (
              <SuggestionCountdown expiresAt={active.expiresAt} />
            )}
          </header>

          {ledgerReplacesAxis ? (
            <SuggestionLedger suggestion={active} />
          ) : (
            <SuggestionAxis suggestion={active} />
          )}

          <SuggestionActions
            suggestion={active}
            disabled={isAnimating}
            onPass={handlePass}
            ledgerOpen={ledgerReplacesAxis}
            onOpenLedger={() => setLedgerOpen(true)}
          />
        </article>
      </div>

      {/* Bottom sheet, mobile only. Portalled per the repo's modal convention so
          the card's transforms cannot become its containing block. */}
      {ledgerOpen &&
        useSheet &&
        createPortal(
          <div
            className="sugg-ledger-sheet-backdrop"
            role="presentation"
            onClick={() => setLedgerOpen(false)}
          >
            <div
              className="sugg-ledger-sheet"
              role="dialog"
              aria-modal="true"
              aria-label="Full ledger"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sugg-ledger-sheet-head">
                <span className="sugg-ledger-sheet-title">Every card, condition and price</span>
                <button
                  type="button"
                  className="sugg-ledger-sheet-close"
                  onClick={() => setLedgerOpen(false)}
                >
                  Hide the full ledger
                </button>
              </div>
              <SuggestionLedger suggestion={active} />
            </div>
          </div>,
          document.body
        )}

      <SuggestionHelpBlock />
    </div>
  );
};

export default SuggestionDeck;
